import { useState } from 'react';
import type { LoaderFunctionArgs, MetaFunction } from 'react-router';
import { redirect, useFetcher } from 'react-router';
import { Alert, AlertDescription } from '~/components/ui/alert';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { getSession } from '~/lib/auth/session';

function GithubIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z" />
    </svg>
  );
}

export const meta: MetaFunction = () => [{ title: 'Sign in — Specly' }];

export async function loader({ request }: LoaderFunctionArgs) {
  const session = await getSession(request);
  if (session) throw redirect('/dashboard');
  return {};
}

export default function Login() {
  const fetcher = useFetcher();
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [githubLoading, setGithubLoading] = useState(false);
  const submitting = fetcher.state === 'submitting';
  const sent =
    fetcher.state === 'idle' && submittedEmail !== '' && !fetcher.data?.error;
  const error = fetcher.data?.error ?? null;

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const email = (form.elements.namedItem('email') as HTMLInputElement).value;
    setSubmittedEmail(email);

    fetcher.submit(
      { email, callbackURL: '/dashboard' },
      {
        method: 'post',
        action: '/api/auth/sign-in/magic-link',
        encType: 'application/json',
      },
    );
  }

  async function handleGithubSignIn() {
    setGithubLoading(true);
    try {
      const res = await fetch('/api/auth/sign-in/social', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider: 'github', callbackURL: '/dashboard' }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } finally {
      setGithubLoading(false);
    }
  }

  return (
    <div className="min-h-[calc(100vh-56px)] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {sent ? (
          <div className="text-center">
            <div className="mb-4 text-4xl">✉️</div>
            <h1 className="text-2xl font-bold">Check your inbox</h1>
            <p className="text-muted-foreground mt-2 text-sm">
              We sent a sign-in link to{' '}
              <span className="font-medium text-foreground">
                {submittedEmail}
              </span>
              .
            </p>
            <p className="text-muted-foreground mt-1 text-sm">
              Click the link in the email to sign in.
            </p>
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold">Sign in</h1>
              <p className="text-muted-foreground mt-1 text-sm">
                Enter your email and we'll send you a sign-in link.
              </p>
            </div>

            <div className="flex flex-col gap-4">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                disabled={githubLoading}
                onClick={handleGithubSignIn}
              >
                <GithubIcon />
                {githubLoading ? 'Redirecting…' : 'Continue with GitHub'}
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    or
                  </span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                  />
                </div>

                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Button type="submit" disabled={submitting} className="w-full">
                  {submitting ? 'Sending…' : 'Send magic link'}
                </Button>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
